import { getTodayDateKeyInTimeZone } from './formatting';

type QueryPrimitive = string | number | boolean | null | undefined;
type QueryValue = QueryPrimitive | QueryPrimitive[];

type BuildQueryParamsOptions = {
  arrayFormat?: 'comma' | 'repeat';
  clampEndDateToTodayWhenShowFutureFalse?: boolean;
  timeZone?: string;
  endDateKey?: string;
  showFutureKey?: string;
  omitFalseForKeys?: string[];
  omitKeys?: string[];
};



const isBlankString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim() === '';

const normalizeArray = (value: QueryPrimitive[]): string[] =>
  value
    .filter((item) => item !== null && item !== undefined)
    .map((item) => String(item).trim())
    .filter((item) => item.length > 0);

const normalizeTags = (value: QueryValue): string[] => {
  if (Array.isArray(value)) return normalizeArray(value);
  if (typeof value === 'string') {
    return value
      .split(',')
      .map((tag) => tag.trim())
      .filter((tag) => tag.length > 0);
  }
  if (value === null || value === undefined) return [];
  return [String(value).trim()].filter((tag) => tag.length > 0);
};

const hasValue = (value: QueryValue): boolean => {
  if (value === null || value === undefined) return false;
  if (Array.isArray(value)) return normalizeArray(value).length > 0;
  if (typeof value === 'string') return value.trim().length > 0;
  return true;
};

export const buildQueryParams = (
  rawParams: Record<string, QueryValue>,
  options: BuildQueryParamsOptions = {}
): string => {
  const {
    arrayFormat = 'comma',
    clampEndDateToTodayWhenShowFutureFalse = false,
    timeZone = 'UTC',
    endDateKey = 'endDate',
    showFutureKey = 'showFuture',
    omitFalseForKeys = [],
    omitKeys = [],
  } = options;

  const params = new URLSearchParams();
  const source: Record<string, QueryValue> = { ...rawParams };

  if (clampEndDateToTodayWhenShowFutureFalse) {
    const showFutureValue = source[showFutureKey];
    const showFuture = showFutureValue === true || showFutureValue === 'true';
    if (!showFuture && !hasValue(source[endDateKey])) {
      source[endDateKey] = getTodayDateKeyInTimeZone(timeZone);
    }
  }

  Object.entries(source).forEach(([key, value]) => {
    if (omitKeys.includes(key) || value === null || value === undefined) return;

    if (key === 'tags') {
      const tags = normalizeTags(value);
      if (tags.length === 0) return;
      params.set('tags', tags.join(','));
      return;
    }

    if (Array.isArray(value)) {
      const normalized = normalizeArray(value);
      if (normalized.length === 0) return;
      if (arrayFormat === 'repeat') {
        normalized.forEach((item) => params.append(key, item));
      } else {
        params.set(key, normalized.join(','));
      }
      return;
    }

    if (isBlankString(value)) return;
    if (omitFalseForKeys.includes(key) && value === false) return;

    params.set(key, String(value));
  });

  return params.toString();
};
